import React, { useCallback, useContext, useEffect, useState } from 'react';
import { X, Sparkles, Calendar, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AuthContext } from '../../context/AuthContext';
import { triggerDiwaliFireworks } from './DiwaliFireworksCanvas';

interface DiwaliGreetingModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const openDiwaliModal = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('open-diwali-modal'));
  }
};

export const DiwaliGreetingModal: React.FC<DiwaliGreetingModalProps> = ({
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
}) => {
  const { isDiwali, toggleDiwali, diwaliSeasonInfo } = useTheme();
  const auth = useContext(AuthContext);
  const isAdmin = auth?.isAdmin ?? false;
  const user = auth?.user ?? null;
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleClose = useCallback(() => {
    if (isControlled && controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalIsOpen(false);
    }
  }, [isControlled, controlledOnClose]);

  useEffect(() => {
    const handleOpen = () => setInternalIsOpen(true);
    window.addEventListener('open-diwali-modal', handleOpen);
    return () => window.removeEventListener('open-diwali-modal', handleOpen);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="ns-diwali-modal-backdrop"
      onClick={handleClose}
      data-testid="diwali-greeting-modal-backdrop"
    >
      <div
        className="ns-diwali-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="diwali-modal-heading"
      >
        <button
          type="button"
          className="ns-diwali-modal__close-btn"
          onClick={handleClose}
          aria-label="Close Diwali celebration modal"
          title="Close (Esc)"
        >
          <X size={16} />
        </button>

        <div className="ns-diwali-modal__hero-diya" aria-hidden="true">
          🪔
        </div>

        <h2 id="diwali-modal-heading" className="ns-diwali-modal__title">
          {diwaliSeasonInfo.formattedGreeting || 'Shubh Deepawali'}
        </h2>

        <p className="ns-diwali-modal__subtitle">
          Wishing you and your family abundance, prosperity, and wealth this festive season.
          May your trades flourish and your portfolios shine bright this new Samvat!
        </p>

        <div className="ns-diwali-modal__info-box">
          <div className="ns-diwali-modal__info-row">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Calendar size={13} /> Diwali Festival Date:
            </span>
            <span className="ns-diwali-modal__info-val">
              {diwaliSeasonInfo.diwaliDateStr || 'Upcoming'}
            </span>
          </div>

          <div className="ns-diwali-modal__info-row">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Sparkles size={13} /> Active Festive Window:
            </span>
            <span className="ns-diwali-modal__info-val">
              10 Days Before → 5 Days After
            </span>
          </div>

          <div className="ns-diwali-modal__info-row">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <CheckCircle2 size={13} /> Theme Status:
            </span>
            <span className="ns-diwali-modal__info-val">
              {isDiwali ? 'Active (Festive Golden Theme)' : 'Standard Theme'}
            </span>
          </div>
        </div>

        {/* Celebration Fireworks & Crackers Trigger */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
          <button
            type="button"
            className="ns-diwali-cracker-btn"
            style={{ fontSize: '0.82rem', padding: '6px 14px' }}
            onClick={() => triggerDiwaliFireworks({ count: 6 })}
            data-testid="burst-crackers-btn"
          >
            <span aria-hidden="true">🎆</span>
            <span>Burst Crackers & Launch Fireworks!</span>
          </button>
        </div>

        {isAdmin && (
          <div className="ns-diwali-modal__admin-section">
            <span className="ns-diwali-modal__admin-badge">
              <ShieldCheck size={12} /> Admin Control (Applies globally to all users)
            </span>
          </div>
        )}

        <div className="ns-diwali-modal__actions">
          {isAdmin ? (
            <>
              <button
                type="button"
                className="ns-diwali-modal__toggle-btn ns-diwali-modal__toggle-btn--primary"
                onClick={() => toggleDiwali(user)}
                data-testid="diwali-toggle-btn"
              >
                {isDiwali ? 'Disable Diwali Theme (All Users)' : 'Enable Diwali Theme (All Users) 🪔'}
              </button>
              <button
                type="button"
                className="ns-diwali-modal__toggle-btn ns-diwali-modal__toggle-btn--secondary"
                onClick={handleClose}
              >
                Done
              </button>
            </>
          ) : (
            <button
              type="button"
              className="ns-diwali-modal__toggle-btn ns-diwali-modal__toggle-btn--primary"
              onClick={handleClose}
            >
              Shubh Deepawali! ✨
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiwaliGreetingModal;
